import { MimeEnum } from "../../../common/commonEnums";
import React, { useState } from "react";
import {
  CancelButton,
  FileFieldError,
  FileFieldErrorText,
  FileInputSuccess,
  FileUploadFormControl,
  FormatBadge,
  UploadDetails,
} from "./styled";
import AudioUpload from "./AudioUpload";
import ImageUpload from "./ImageUpload";
import { useField, useFormikContext } from "formik";
import {
  Paragraph16Bold,
  Paragraph16Regular,
} from "../../../common/Typography/Paragraph16";
import { useFileUpload } from "./useFileUpload";
import { IFile } from "../../../common/commonTypes";
import FileUploadProgress from "./FileUploadProgress";
import Icon from "../../../common/components/Icon/Icon";
import { useTranslation } from "react-i18next";
import { validateSingleFile } from "./UploadTrackSchema";
import { Truncate } from "../../../common/components/styled";
import {
  extendFile,
  getDefaultExtendedFile,
} from "../../../helpers/fileHelper";
import { ClearErrorButton } from "../../../common/components/FormControls";

export enum FileUploadType {
  AUDIO,
  IMAGE,
}

interface BaseFileUploadProps {
  description?: string;
  fileFormats: MimeEnum[];
  name: string;
  useCloudinaryVideo?: boolean;
}

interface FileUploadWrapperProps extends BaseFileUploadProps {
  onFileUploaded?: (file: IFile) => void;
  onFileDeleted?: () => void;
  type: FileUploadType;
  label?: string;
  onUploadStart: () => void;
  onUploadEnd: () => void;
  thumbnailSizes?: number[];
  errorMessageOptions?: {
    [key: string]: any;
  };
  [prop: string]: any;
}

export interface FileUploadProps extends BaseFileUploadProps {
  [prop: string]: any;
}

const FileUpload = ({
  fileFormats,
  description,
  name,
  onFileUploaded,
  onFileDeleted,
  type,
  label,
  errorMessageOptions,
  onUploadStart,
  onUploadEnd,
  thumbnailSizes,
  useCloudinaryVideo = false,
  ...rest
}: FileUploadWrapperProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { validateField } = useFormikContext();
  const field = useField({ name });
  const meta = field[1];
  const helpers = field[2];
  const { uploadFile, deleteFile, progress, size, isLoading } = useFileUpload();
  const { t } = useTranslation();
  const { value, error, touched } = meta;
  const { setValue, setError, setTouched } = helpers;

  const resetField = () => {
    setError(undefined);
    setTouched(false, false);
    setValue("", false);
  };

  const clearFile = () => {
    resetField();
    deleteFile();
    onFileDeleted?.();
  };

  const processFile = async (originalFile: File) => {
    try {
      // first validation is only for checking size and type which are available instantly
      // We want to check it before processing file to get duration/dimensions which could be expensive
      const isUnprocessedFileValid = await validateSingleFile(
        name,
        getDefaultExtendedFile(originalFile)
      );

      if (!isUnprocessedFileValid) {
        await setValue(getDefaultExtendedFile(originalFile), false);
        await setTouched(true, true);
        validateField(name);
        return;
      }

      // process file to get duration/dimensions and validate it again
      setIsProcessing(true);
      const extendedFile = await extendFile(originalFile);
      setIsProcessing(false);

      await setValue(extendedFile, false);
      await setTouched(true, true);
      validateField(name);

      const isValid = await validateSingleFile(name, extendedFile);
      if (isValid) {
        await uploadFile(
          extendedFile.file,
          useCloudinaryVideo,
          onFileUploaded,
          thumbnailSizes
        );
      }
    } catch (error) {
      setIsProcessing(false);
    }
  };

  const handleOnChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const originalFile = event.target?.files?.[0];

    if (originalFile) {
      onUploadStart();
      await processFile(originalFile);
      onUploadEnd();
    }

    // clear target value to allow uploading same file again
    event.target.value = "";
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <FileUploadProgress
          label={label}
          progress={progress}
          title={value?.name?.toString()}
          size={size}
          onCancel={clearFile}
        />
      );
    }

    if (progress === 100) {
      return (
        <FileInputSuccess>
          <div className="left">
            <Icon name="greenTick" width="32px" height="32px" />
            <UploadDetails className="details">
              <Paragraph16Bold>{description}</Paragraph16Bold>
              <Truncate>
                <Paragraph16Regular>
                  {value?.file?.name?.toString()}
                </Paragraph16Regular>
              </Truncate>
              {label && <FormatBadge>{label}</FormatBadge>}
            </UploadDetails>
          </div>
          <CancelButton onClick={clearFile} />
        </FileInputSuccess>
      );
    }

    let Component = AudioUpload;

    if (type === FileUploadType.IMAGE) {
      Component = ImageUpload;
    }

    return (
      <Component
        fileFormats={fileFormats}
        description={description}
        name={name}
        onChange={handleOnChange}
        label={label}
        error={error && touched}
        isProcessing={isProcessing}
        {...rest}
      />
    );
  };

  return (
    // id is needed for form scroll to error feature
    <FileUploadFormControl id={name}>
      {renderContent()}
      {error && touched && (
        <FileFieldError>
          <FileFieldErrorText size="md">
            {t(error.toString(), errorMessageOptions)}
          </FileFieldErrorText>
          {!!value && <ClearErrorButton onClick={clearFile} />}
        </FileFieldError>
      )}
    </FileUploadFormControl>
  );
};

export default FileUpload;
